import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUSES = ["Submitted", "Under Review", "Contacted", "Counselling", "Documents Pending", "Approved", "Fee Pending", "Enrolled", "Rejected", "Withdrawn"];

export default function AdminApplications() {
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [status, setStatus] = useState("all");

  const load = () => {
    const params = { limit: 50 };
    if (status !== "all") params.status = status;
    api.get("/admin/applications", { params }).then((r) => setData(r.data)).catch(() => {});
  };

  useEffect(load, [status]);

  const updateStatus = async (app, newStatus) => {
    try {
      await api.patch(`/admin/applications/${app.id}`, { status: newStatus });
      toast.success(`${app.application_id} → ${newStatus}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-applications-page">
      <h1 className="font-display text-2xl font-bold text-white mb-1">Admission Applications</h1>
      <p className="text-xs text-slate-500 mb-8">{data.total} applications. Move each through the admission workflow.</p>

      <div className="mb-6">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-52 bg-navy-surface border-white/15 text-slate-200" data-testid="applications-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-navy-surface border-white/10 text-slate-200">
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-4" data-testid="applications-list">
        {data.items.length === 0 && (
          <div className="bg-card border border-white/10 rounded-2xl p-10 text-center text-sm text-slate-500">No applications in this view.</div>
        )}
        {data.items.map((a) => (
          <div key={a.id} className="bg-card border border-white/10 rounded-2xl p-6" data-testid={`application-card-${a.application_id}`}>
            <div className="flex flex-wrap justify-between gap-4 mb-4">
              <div>
                <div className="font-mono text-orange-400 text-sm">{a.application_id}</div>
                <div className="font-display text-lg font-semibold text-white">{a.full_name}</div>
                <div className="text-xs text-slate-500">{a.course_title} • {a.mode} • {a.qualification}</div>
              </div>
              <Select value={a.status} onValueChange={(v) => updateStatus(a, v)}>
                <SelectTrigger className="w-44 bg-navy-base border-white/15 text-slate-200 text-xs" data-testid={`application-status-${a.application_id}`}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-navy-surface border-white/10 text-slate-200">
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-slate-400">
              <span>Phone: {a.phone}</span>
              <span>Email: {a.email}</span>
              <span>{a.district}, {a.state}</span>
              <span>Via: {a.referral_source}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
